'use client';

import { styled } from 'styled-components';
import { CtaProps } from '@types';
import { Cta } from './AccountCtas';
import Link from 'next/link';
import WistiaLogo from '@dvd/components/WistiaLogo';
import { trackLink } from '@lib/tracking';

type MiniNavProps = {
  /**
   * Optional primary CTA
   */
  primaryCta?: CtaProps;
  /**
   * Optional secondary CTA
   */
  secondaryCta?: CtaProps;
};

const MiniNavContainer = styled.nav`
  background-color: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.blue200};
  left: 0;
  position: sticky;
  right: 0;
  top: 0;
  z-index: 999;
  margin-inline: auto;
`;

const MiniNavButtonRow = styled.div`
  display: flex;
  align-items: center;

  & .mini-nav-secondary-cta {
    display: none;
  }

  ${({ theme }) => theme.mq.minWidth.medium} {
    & .mini-nav-secondary-cta {
      display: inline-block;
    }
    & .mini-nav-primary-cta {
      margin-inline-start: ${({ theme }) => theme.spacing[0.75]};
    }
  }
`;

const MiniNavContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 auto;
  padding-inline: ${({ theme }) => theme.spacing[1.5]};

  ${({ theme }) => theme.mq.minWidth.medium} {
    padding-inline: ${({ theme }) => theme.grid.margin.medium};
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    padding-inline: ${({ theme }) => theme.grid.margin.large};
  }

  /** custom per design */
  padding-block: 9px;
  flex-direction: row;
  max-width: ${({ theme }) => theme.breakpoints.xl};
`;

const Logo = styled(WistiaLogo)`
  /** custom per design */
  height: 20px;
  margin-inline-end: ${({ theme }) => theme.spacing[1]};
`;

/**
 * The MiniNav component is a navigation component that displays a logo and up to two optional CTAs.
 * It is used on the landing pages.
 * If there is one cta it will be primary; if there are two the second will be secondary
 * Copy note: Max characters for the primary cta is ~20 characters including spaces.
 * TODO: Consider adding a max character limit for all CtaProps
 */
const MiniNav = ({ primaryCta = null, secondaryCta = null }: MiniNavProps) => {
  const handleLogoClick = () => {
    trackLink('logo', 'mini-nav');
  };

  return (
    <MiniNavContainer>
      <MiniNavContent>
        <Link
          aria-label="Wistia homepage"
          href="/"
          tabIndex={0}
          onClick={handleLogoClick}
        >
          <Logo isOptical={true} />
        </Link>
        {primaryCta && (
          <MiniNavButtonRow>
            {secondaryCta && (
              <Cta
                className="mini-nav-secondary-cta"
                color="blue500"
                href={secondaryCta.href}
                size="mini"
                trackingLabel={secondaryCta.trackingLabel}
                trackingProperty={secondaryCta.trackingProperty}
                variant="secondary"
              >
                {secondaryCta?.text}
              </Cta>
            )}
            <Cta
              className="mini-nav-primary-cta"
              color="blue500"
              href={primaryCta.href}
              size="mini"
              trackingLabel={primaryCta.trackingLabel}
              trackingProperty={primaryCta.trackingProperty}
            >
              {primaryCta.text}
            </Cta>
          </MiniNavButtonRow>
        )}
      </MiniNavContent>
    </MiniNavContainer>
  );
};

export default MiniNav;
